import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, AccessibleCompany } from '@/types/user';

interface AuthState {
  token: string | null;
  user: User | null;
  accessibleCompanies: AccessibleCompany[];
  isAuthenticated: boolean;

  setAuth: (token: string, user: User, companies: AccessibleCompany[]) => void;
  updateUser: (user: Partial<User>) => void;
  logout: () => void;

  isSuperAdmin: () => boolean;
  canAccessCompany: (companyId: number) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      accessibleCompanies: [],
      isAuthenticated: false,

      setAuth: (token, user, companies) => set({
        token,
        user,
        accessibleCompanies: companies,
        isAuthenticated: true,
      }),

      updateUser: (updatedUser) => set((state) => ({
        user: state.user ? { ...state.user, ...updatedUser } : null,
      })),

      logout: () => set({
        token: null,
        user: null,
        accessibleCompanies: [],
        isAuthenticated: false,
      }),

      isSuperAdmin: () => {
        const { user } = get();
        return user?.role === 'super_admin';
      },

      canAccessCompany: (companyId) => {
        const { user, accessibleCompanies } = get();
        if (user?.role === 'super_admin') return true;
        return accessibleCompanies.some(c => c.id === companyId);
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        accessibleCompanies: state.accessibleCompanies,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
